import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import LibraryBooksOutlined from '@mui/icons-material/LibraryBooksOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { Title, useNotify, useTranslate } from 'react-admin';
import {
    KNOWLEDGE_JURISDICTIONS, KNOWLEDGE_LICENSES, knowledgeService,
    type KnowledgeHit, type KnowledgeLicense, type KnowledgeScope, type KnowledgeSource,
} from '../../services/knowledgeService';
import '../AiGenerator/ai-generator.css';
import './knowledge-base.css';

type AddMode = 'file' | 'url' | 'text';

const errorMessage = (e: unknown, fallback: string): string => {
    const data = (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
    return data?.message ?? data?.error ?? fallback;
};

/**
 * Tranche C2 (#489): Wissensbasis — company admins manage their own sources
 * (`scope="company"`), operators the platform sources (`scope="platform"`).
 * Same page, different endpoints.
 */
export const KnowledgeBasePage: React.FC<{ scope?: KnowledgeScope }> = ({ scope = 'company' }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [sources, setSources] = useState<KnowledgeSource[]>([]);
    const [platformCount, setPlatformCount] = useState<number | undefined>();
    const [embeddingsAvailable, setEmbeddingsAvailable] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [mode, setMode] = useState<AddMode>('file');
    const [file, setFile] = useState<File | null>(null);
    const [url, setUrl] = useState('');
    const [text, setText] = useState('');
    const [meta, setMeta] = useState({ title: '', publisher: '', documentNumber: '', jurisdiction: 'DE', license: 'internal' as KnowledgeLicense, validFrom: '', supersedesId: '' });
    const [submitting, setSubmitting] = useState(false);

    const [query, setQuery] = useState('');
    const [hits, setHits] = useState<KnowledgeHit[] | null>(null);
    const [searching, setSearching] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await knowledgeService.list(scope);
            setSources(data.sources);
            setPlatformCount(data.platformCount);
            setEmbeddingsAvailable(data.embeddingsAvailable);
        } catch (e) {
            setError(errorMessage(e, translate('knowledgeBase.errors.generic')));
        } finally {
            setLoading(false);
        }
    }, [scope, translate]);

    useEffect(() => { void load(); }, [load]);

    // Poll while something is being processed.
    useEffect(() => {
        if (!sources.some((s) => s.status === 'pending' || s.status === 'processing')) return undefined;
        const timer = window.setInterval(() => { void load(); }, 5000);
        return () => window.clearInterval(timer);
    }, [sources, load]);

    const canSubmit = !submitting && meta.title.trim() !== '' && ((mode === 'file' && !!file) || (mode === 'url' && url.trim() !== '') || (mode === 'text' && text.trim().length >= 20));

    const submit = async () => {
        setSubmitting(true);
        try {
            const base = {
                title: meta.title.trim(),
                license: meta.license,
                publisher: meta.publisher.trim() || undefined,
                documentNumber: meta.documentNumber.trim() || undefined,
                jurisdiction: meta.jurisdiction,
                validFrom: meta.validFrom || undefined,
                supersedesId: meta.supersedesId ? Number(meta.supersedesId) : null,
            };
            const payload = mode === 'file' ? { ...base, file: file as File } : mode === 'url' ? { ...base, url: url.trim() } : { ...base, text };
            await knowledgeService.create(payload, scope);
            notify(translate('knowledgeBase.add.created'), { type: 'success' });
            setFile(null);
            setUrl('');
            setText('');
            setMeta({ ...meta, title: '', documentNumber: '', supersedesId: '' });
            await load();
        } catch (e) {
            const status = (e as { response?: { status?: number } })?.response?.status;
            notify(status === 409 ? translate('knowledgeBase.errors.duplicate') : errorMessage(e, translate('knowledgeBase.errors.generic')), { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (source: KnowledgeSource) => {
        if (!window.confirm(translate('knowledgeBase.list.confirmDelete', { title: source.title }))) return;
        try {
            await knowledgeService.remove(source.id, scope);
            notify(translate('knowledgeBase.list.deleted'), { type: 'info' });
            await load();
        } catch (e) {
            notify(errorMessage(e, translate('knowledgeBase.errors.generic')), { type: 'error' });
        }
    };

    const reingest = async (source: KnowledgeSource) => {
        try {
            await knowledgeService.reingest(source.id, scope);
            notify(translate('knowledgeBase.list.reingested'), { type: 'info' });
            await load();
        } catch (e) {
            notify(errorMessage(e, translate('knowledgeBase.errors.generic')), { type: 'error' });
        }
    };

    const search = async () => {
        if (query.trim() === '') return;
        setSearching(true);
        try {
            setHits(await knowledgeService.search(query.trim(), { limit: 8 }, scope));
        } catch (e) {
            notify(errorMessage(e, translate('knowledgeBase.errors.generic')), { type: 'error' });
        } finally {
            setSearching(false);
        }
    };

    const statusColor = (status: KnowledgeSource['status']): 'default' | 'success' | 'error' | 'warning' => (status === 'ready' ? 'success' : status === 'failed' ? 'error' : 'warning');

    return (
        <Box className="oa-ai-generator oa-knowledge-base">
            <Title title={translate(scope === 'platform' ? 'knowledgeBase.platformTitle' : 'knowledgeBase.pageTitle')} />
            <Box className="oa-aig-header">
                <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LibraryBooksOutlined /> {translate(scope === 'platform' ? 'knowledgeBase.platformTitle' : 'knowledgeBase.pageTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{translate(scope === 'platform' ? 'knowledgeBase.platformIntro' : 'knowledgeBase.intro')}</Typography>
                {scope === 'company' && platformCount !== undefined && <Chip size="small" sx={{ mt: 1 }} label={translate('knowledgeBase.platformCount', { count: platformCount })} />}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!embeddingsAvailable && !loading && <Alert severity="info" sx={{ mb: 2 }}>{translate('knowledgeBase.embeddingsOff')}</Alert>}

            <div className="oa-aig-card" data-testid="knowledge-add">
                <h2 className="oa-aig-card-title"><span className="oa-aig-icon"><UploadFileOutlined fontSize="small" /></span>{translate('knowledgeBase.add.title')}</h2>
                <Tabs value={mode} onChange={(_, v: AddMode) => setMode(v)} sx={{ mb: 1.5 }}>
                    <Tab value="file" label={translate('knowledgeBase.add.file')} />
                    <Tab value="url" label={translate('knowledgeBase.add.url')} />
                    <Tab value="text" label={translate('knowledgeBase.add.text')} />
                </Tabs>
                {mode === 'file' && (
                    <Box sx={{ mb: 1.5 }}>
                        <Button component="label" variant="outlined" size="small" startIcon={<UploadFileOutlined />}>
                            {file ? file.name : translate('knowledgeBase.add.file')}
                            <input hidden type="file" accept=".pdf,.docx,.pptx,.md,.markdown,.txt" data-testid="knowledge-file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{translate('knowledgeBase.add.fileHint')}</Typography>
                    </Box>
                )}
                {mode === 'url' && <TextField fullWidth size="small" sx={{ mb: 1.5 }} label={translate('knowledgeBase.add.urlLabel')} value={url} onChange={(e) => setUrl(e.target.value)} inputProps={{ 'aria-label': translate('knowledgeBase.add.urlLabel') }} />}
                {mode === 'text' && <TextField fullWidth multiline minRows={4} size="small" sx={{ mb: 1.5 }} label={translate('knowledgeBase.add.textLabel')} value={text} onChange={(e) => setText(e.target.value)} inputProps={{ 'aria-label': translate('knowledgeBase.add.textLabel') }} />}
                <div className="oa-aig-form-row">
                    <TextField size="small" required label={translate('knowledgeBase.fields.title')} value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} inputProps={{ maxLength: 200, 'aria-label': translate('knowledgeBase.fields.title') }} />
                    <TextField size="small" label={translate('knowledgeBase.fields.documentNumber')} value={meta.documentNumber} onChange={(e) => setMeta({ ...meta, documentNumber: e.target.value })} inputProps={{ maxLength: 120 }} />
                </div>
                <div className="oa-aig-form-row">
                    <TextField size="small" label={translate('knowledgeBase.fields.publisher')} value={meta.publisher} onChange={(e) => setMeta({ ...meta, publisher: e.target.value })} inputProps={{ maxLength: 200 }} />
                    <TextField select size="small" label={translate('knowledgeBase.fields.license')} value={meta.license} onChange={(e) => setMeta({ ...meta, license: e.target.value as KnowledgeLicense })} inputProps={{ 'aria-label': translate('knowledgeBase.fields.license') }}>
                        {KNOWLEDGE_LICENSES.map((l) => <MenuItem key={l} value={l}>{translate(`knowledgeBase.license.${l}`)}</MenuItem>)}
                    </TextField>
                </div>
                <div className="oa-aig-form-row">
                    <TextField select size="small" label={translate('knowledgeBase.fields.jurisdiction')} value={meta.jurisdiction} onChange={(e) => setMeta({ ...meta, jurisdiction: e.target.value })}>
                        {KNOWLEDGE_JURISDICTIONS.map((j) => <MenuItem key={j} value={j}>{j}</MenuItem>)}
                    </TextField>
                    <TextField size="small" type="date" label={translate('knowledgeBase.fields.validFrom')} value={meta.validFrom} onChange={(e) => setMeta({ ...meta, validFrom: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
                    <TextField select size="small" label={translate('knowledgeBase.fields.supersedes')} value={meta.supersedesId} onChange={(e) => setMeta({ ...meta, supersedesId: e.target.value })}>
                        <MenuItem value="">–</MenuItem>
                        {sources.filter((s) => s.isCurrent).map((s) => <MenuItem key={s.id} value={String(s.id)}>{s.title} (v{s.version})</MenuItem>)}
                    </TextField>
                </div>
                <Typography variant="caption" color="text.secondary">{translate('knowledgeBase.licenseHint')}</Typography>
                <div className="oa-aig-actions">
                    <Button variant="contained" onClick={() => void submit()} disabled={!canSubmit} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <UploadFileOutlined />}>
                        {translate('knowledgeBase.add.submit')}
                    </Button>
                </div>
            </div>

            <div className="oa-aig-card" data-testid="knowledge-list">
                <h2 className="oa-aig-card-title"><span className="oa-aig-icon"><LibraryBooksOutlined fontSize="small" /></span>{translate(scope === 'platform' ? 'knowledgeBase.platformTitle' : 'knowledgeBase.pageTitle')}</h2>
                {loading && sources.length === 0 && <CircularProgress size={24} />}
                {!loading && sources.length === 0 && <Typography variant="body2" color="text.secondary">{translate('knowledgeBase.list.empty')}</Typography>}
                <ul className="oa-kb-list">
                    {sources.map((source) => (
                        <li key={source.id} className={`oa-kb-item${source.isCurrent ? '' : ' oa-kb-item--retired'}`} data-testid={`knowledge-source-${source.id}`}>
                            <div className="oa-kb-item-main">
                                <div className="oa-kb-item-title">
                                    <strong>{source.title}</strong>
                                    {source.documentNumber && <span className="oa-kb-muted"> · {source.documentNumber}</span>}
                                    {source.publisher && <span className="oa-kb-muted"> · {source.publisher}</span>}
                                </div>
                                <div className="oa-kb-item-meta">
                                    <Chip size="small" color={statusColor(source.status)} label={translate(`knowledgeBase.status.${source.status}`)} />
                                    <Chip size="small" variant="outlined" label={translate(`knowledgeBase.license.${source.license}`)} />
                                    <Chip size="small" variant="outlined" label={translate('knowledgeBase.list.version', { version: source.version })} />
                                    {!source.isCurrent && <Chip size="small" color="default" label={translate('knowledgeBase.list.retired')} />}
                                    {source.status === 'ready' && !source.hasEmbeddings && <Chip size="small" variant="outlined" label={translate('knowledgeBase.list.noEmbeddings')} />}
                                    <span className="oa-kb-muted">{translate('knowledgeBase.list.chunks', { count: source.chunkCount })} · {translate('knowledgeBase.list.characters', { count: source.characterCount.toLocaleString('de-DE') })}</span>
                                    {(source.referenceCount ?? 0) > 0 && <span className="oa-kb-muted">· {translate('knowledgeBase.list.references', { count: source.referenceCount })}</span>}
                                </div>
                                {source.errorMessage && <Typography variant="caption" color="error">{source.errorMessage}</Typography>}
                                <Typography variant="body2" color="text.secondary" className="oa-kb-preview">{source.preview}</Typography>
                            </div>
                            <div className="oa-kb-item-actions">
                                <Tooltip title={translate('knowledgeBase.list.reingest')}><IconButton size="small" aria-label={translate('knowledgeBase.list.reingest')} onClick={() => void reingest(source)}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title={translate('knowledgeBase.list.delete')}><IconButton size="small" aria-label={translate('knowledgeBase.list.delete')} onClick={() => void remove(source)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="oa-aig-card" data-testid="knowledge-search">
                <h2 className="oa-aig-card-title"><span className="oa-aig-icon"><SearchOutlined fontSize="small" /></span>{translate('knowledgeBase.search.title')}</h2>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('knowledgeBase.search.hint')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField fullWidth size="small" placeholder={translate('knowledgeBase.search.placeholder')} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void search(); }} inputProps={{ 'aria-label': translate('knowledgeBase.search.placeholder') }} />
                    <Button variant="outlined" onClick={() => void search()} disabled={searching || query.trim() === ''}>{translate('knowledgeBase.search.button')}</Button>
                </Box>
                {hits !== null && hits.length === 0 && <Typography variant="body2" color="text.secondary">{translate('knowledgeBase.search.empty')}</Typography>}
                {hits && hits.map((hit, i) => (
                    <div key={hit.chunkId} className="oa-kb-hit">
                        <div className="oa-kb-hit-head"><Chip size="small" label={`S${i + 1}`} /> <strong>{hit.citation}</strong> <span className="oa-kb-muted">({hit.scope === 'platform' ? translate('knowledgeBase.platformTitle') : translate('knowledgeBase.pageTitle')}, score {hit.score.toFixed(4)})</span></div>
                        <Typography variant="body2" className="oa-kb-hit-text">{hit.content.slice(0, 600)}{hit.content.length > 600 ? ' …' : ''}</Typography>
                    </div>
                ))}
            </div>
        </Box>
    );
};

export default KnowledgeBasePage;
